"use client";

import React, { createContext, useContext, useCallback, useReducer, useRef } from "react";
import { io, Socket } from "socket.io-client";

// ─── Types ─────────────────────────────────────────────────────────────────

export interface SyncError {
    message: string;
    detail?: string;
}

export interface SyncResult {
    success: boolean;
    totalContacts: number;
    synced: number;
    withName: number;
    withNotify: number;
    cleanedNames: number;
    duration: string;
    errors?: SyncError[];
    nextSync?: string;
    unnamedAfter?: number;
    unnamedBefore?: number;
    batches?: number;
}

export type SyncStatus = "idle" | "syncing" | "success" | "error";

export interface SyncState {
    status: SyncStatus;
    sessionId: string | null;
    total: number;          // toplam isimsiz contact
    current: number;        // şu ana kadar işlenen
    result: SyncResult | null;
    errorMessage: string | null;
    errors: SyncError[];
    startTime: number;
    elapsed: number;
}

type SyncAction =
    | { type: "START"; sessionId: string; total: number }
    | { type: "PROGRESS"; current: number; total?: number }
    | { type: "COMPLETE"; result: SyncResult }
    | { type: "ERROR"; message: string }
    | { type: "DISMISS" };

function syncReducer(state: SyncState, action: SyncAction): SyncState {
    switch (action.type) {
        case "START":
            return {
                status: "syncing",
                sessionId: action.sessionId,
                total: action.total,
                current: 0,
                result: null,
                errorMessage: null,
                errors: [],
                startTime: Date.now(),
                elapsed: 0,
            };
        case "PROGRESS":
            return {
                ...state,
                current: action.current,
                ...(action.total !== undefined ? { total: action.total } : {}),
            };
        case "COMPLETE":
            return {
                ...state,
                status: "success",
                total: action.result.unnamedBefore || action.result.totalContacts || state.total,
                current: action.result.unnamedBefore || action.result.totalContacts || state.total,
                result: action.result,
                errors: action.result.errors || [],
                elapsed: parseFloat(action.result.duration) || (Date.now() - state.startTime) / 1000,
            };
        case "ERROR":
            return {
                ...state,
                status: "error",
                errorMessage: action.message,
                elapsed: (Date.now() - state.startTime) / 1000,
            };
        case "DISMISS":
            return {
                status: "idle",
                sessionId: null,
                total: 0,
                current: 0,
                result: null,
                errorMessage: null,
                errors: [],
                startTime: 0,
                elapsed: 0,
            };
    }
}

const initialState: SyncState = {
    status: "idle",
    sessionId: null,
    total: 0,
    current: 0,
    result: null,
    errorMessage: null,
    errors: [],
    startTime: 0,
    elapsed: 0,
};

// ─── Context ────────────────────────────────────────────────────────────────

interface SyncContextValue {
    state: SyncState;
    startSync: (sessionId: string) => Promise<SyncResult | null>;
    dismissSync: () => void;
}

const SyncContext = createContext<SyncContextValue | null>(null);

export function useSync() {
    const ctx = useContext(SyncContext);
    if (!ctx) throw new Error("useSync must be used within <SyncProvider>");
    return ctx;
}

// ─── Provider ───────────────────────────────────────────────────────────────

export function SyncProvider({ children }: { children: React.ReactNode }) {
    const [state, dispatch] = useReducer(syncReducer, initialState);
    const fetchControllerRef = useRef<AbortController | null>(null);
    const syncSocketRef = useRef<Socket | null>(null);

    const startSync = useCallback(async (sessionId: string): Promise<SyncResult | null> => {
        // Önceki sync'i temizle
        if (fetchControllerRef.current) {
            fetchControllerRef.current.abort();
        }
        if (syncSocketRef.current) {
            syncSocketRef.current.disconnect();
            syncSocketRef.current = null;
        }

        // Başlangıç — total henüz bilinmiyor
        dispatch({ type: "START", sessionId, total: 0 });

        // Sync ilerlemesi için Socket.IO dinle
        const syncSocket = io({
            path: "/api/socket/io",
            addTrailingSlash: false,
        });
        syncSocketRef.current = syncSocket;

        syncSocket.on("connect", () => {
            syncSocket.emit("join-session", sessionId);
        });

        // Backend'den batch ilerlemesi dinle
        syncSocket.on("sync.progress", (data: { processed: number; total: number; synced: number }) => {
            dispatch({ type: "PROGRESS", current: data.processed, total: data.total });
        });

        const controller = new AbortController();
        fetchControllerRef.current = controller;

        try {
            const res = await fetch(`/api/contacts/${sessionId}/sync`, {
                method: "POST",
                signal: controller.signal,
            });

            if (!res.ok) {
                const errBody = await res.json().catch(() => ({}));
                throw new Error(errBody.message || errBody.error || `HTTP ${res.status}`);
            }

            const data: SyncResult = await res.json();

            if (data.success) {
                const mappedErrors: SyncError[] = (data as any).errors?.map((e: any) => ({
                    message: e.jid ? `${e.jid}: ${e.error}` : e.error,
                    detail: e.error
                })) || [];
                const mappedData = { ...data, errors: mappedErrors };
                dispatch({ type: "COMPLETE", result: mappedData });
                return data;
            } else {
                dispatch({
                    type: "ERROR",
                    message: data.errors?.[0]?.message || "Sync başarısız oldu",
                });
                return null;
            }
        } catch (err: unknown) {
            if (err instanceof Error && err.name === "AbortError") return null;
            dispatch({
                type: "ERROR",
                message: err instanceof Error ? err.message : "Sync sırasında hata oluştu",
            });
            return null;
        } finally {
            fetchControllerRef.current = null;
            // Sync socket'i bırak — ana chat socket ayrı
            if (syncSocketRef.current) {
                syncSocketRef.current.disconnect();
                syncSocketRef.current = null;
            }
        }
    }, []);

    const dismissSync = useCallback(() => {
        if (fetchControllerRef.current) {
            fetchControllerRef.current.abort();
        }
        if (syncSocketRef.current) {
            syncSocketRef.current.disconnect();
            syncSocketRef.current = null;
        }
        dispatch({ type: "DISMISS" });
    }, []);

    return (
        <SyncContext.Provider value={{ state, startSync, dismissSync }}>
            {children}
        </SyncContext.Provider>
    );
}
